///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { TopologyLinkFeatureDTO, TopologyLinkFeatureFlagsIndexes, TopologyLinkTypeEnum } from './topology-types.model';
import { LinkFeature } from '../../../project-view-panel/logical-topology/entities/link/link-feature';
import { SectorPolarityEnum } from '../../../generated/shared-types.model';

export class GtthLinkFeatureDtoModel extends TopologyLinkFeatureDTO {
    constructor(
        public n0: number,
        public n1: number,
        public c: string,
        public si: number,
        public sai: number,
        public sd: number,
        public sid: number,
        public sap: number,
        public f: number[],
        public tx: number,
        public rx: number,
        public pr0: number,
        public pr1: number,
        public a0: number,
        public a1: number,
        public t: number,
        public an0: string,
        public an1: string,
        public ip0: string,
        public ip1: string,
        public sp: SectorPolarityEnum
    ) {
        super();
        if (!this.f) {
            this.f = [];
        }
    }

    public static fromData(data: TopologyLinkFeatureDTO): GtthLinkFeatureDtoModel {
        return new GtthLinkFeatureDtoModel(
            data.n0,
            data.n1,
            data.c,
            data.si,
            data.sai,
            data.sd,
            data.sid,
            data.sap,
            data.f,
            data.tx,
            data.rx,
            data.pr0,
            data.pr1,
            data.a0,
            data.a1,
            data.t,
            data.an0,
            data.an1,
            data.ip0,
            data.ip1,
            data.sp
        );
    }

    public static fromStationsIds(fromId: number, toId: number, linkType: TopologyLinkTypeEnum): GtthLinkFeatureDtoModel {
        const linkDto = new GtthLinkFeatureDtoModel(
            fromId,
            toId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            [],
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            linkType,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
        );
        linkDto.setToRemove(false);
        linkDto.setIsActive(false);
        linkDto.setIsInstalled(false);
        linkDto.setIsUserForced(false);
        linkDto.setIsDisabled(false);
        linkDto.setFrequencyForced(false);
        linkDto.setSectorIdx(null);
        linkDto.setSectorApIdx(null);
        linkDto.setSectorDirection(null);
        return linkDto;
    }

    public notFrozenAttributesEquals(link: GtthLinkFeatureDtoModel): boolean {
        return this.getId() === link.getId() && this.getLinkType() === link.getLinkType() && this.getIsDisabled() === link.getIsDisabled();
    }

    public getId(): number {
        return LinkFeature.generateLinkId(this.n0, this.n1);
    }

    public getNode0IdCustom(): number {
        return this.n0;
    }

    public setNode0IdCustom(nodeId: number) {
        this.n0 = nodeId;
    }

    public getNode1IdCustom(): number {
        return this.n1;
    }

    public setNode1IdCustom(nodeId: number) {
        this.n1 = nodeId;
    }

    public getUsedProd0(): number {
        return this.pr0;
    }

    public setUsedProd0(value: number) {
        this.pr0 = value;
    }

    public getUsedProd1(): number {
        return this.pr1;
    }

    public setUsedProd1(value: number) {
        this.pr1 = value;
    }

    public getUsedAnt0(): number {
        return this.a0;
    }

    public setUsedAnt0(value: number) {
        this.a0 = value;
    }

    public getUsedAnt1(): number {
        return this.a1;
    }

    public setUsedAnt1(value: number) {
        this.a1 = value;
    }

    public getToRemove(): boolean {
        return this.f[TopologyLinkFeatureFlagsIndexes.TO_REMOVE] === 1;
    }

    public setToRemove(value: boolean) {
        this.f[TopologyLinkFeatureFlagsIndexes.TO_REMOVE] = value ? 1 : 0;
    }

    public isFrequencyForced(): boolean {
        return this.f[TopologyLinkFeatureFlagsIndexes.IS_FREQUENCY_FORCED] === 1;
    }

    public setFrequencyForced(value: boolean) {
        this.f[TopologyLinkFeatureFlagsIndexes.IS_FREQUENCY_FORCED] = value ? 1 : 0;
    }

    public getTxFreq(): number {
        return this.tx;
    }

    public setTxFreq(value: number): void {
        this.tx = value;
    }

    public getRxFreq(): number {
        return this.rx;
    }

    public setRxFreq(value: number): void {
        this.rx = value;
    }

    public getIsActive(): boolean {
        return this.f[TopologyLinkFeatureFlagsIndexes.IS_ACTIVE] === 1;
    }

    public setIsActive(value: boolean) {
        this.f[TopologyLinkFeatureFlagsIndexes.IS_ACTIVE] = value ? 1 : 0;
    }

    public getIsInstalled(): boolean {
        return this.f[TopologyLinkFeatureFlagsIndexes.IS_INSTALLED] === 1;
    }

    public setIsInstalled(value: boolean) {
        this.f[TopologyLinkFeatureFlagsIndexes.IS_INSTALLED] = value ? 1 : 0;
    }

    public getIsUserForced(): boolean {
        return this.f[TopologyLinkFeatureFlagsIndexes.IS_USER_FORCED] === 1;
    }

    public setIsUserForced(value: boolean) {
        this.f[TopologyLinkFeatureFlagsIndexes.IS_USER_FORCED] = value ? 1 : 0;
    }

    public getIsDisabled(): boolean {
        return this.f[TopologyLinkFeatureFlagsIndexes.IS_DISABLED] === 1;
    }

    public setIsDisabled(value: boolean) {
        this.f[TopologyLinkFeatureFlagsIndexes.IS_DISABLED] = value ? 1 : 0;
    }

    public getP2mpRepSectorIdx(): number {
        return this.sai;
    }

    public setP2mpRepSectorIdx(index: number): void {
        this.sai = index;
    }

    public getSectorIdx(): number {
        return this.si;
    }

    public setSectorIdx(value: number) {
        this.si = value;
    }

    public getSectorApIdx(): number {
        return this.sap;
    }

    public setSectorApIdx(nodeId: number) {
        this.sap = nodeId;
    }

    public getSectorDirection(): number {
        return this.sd;
    }

    public setSectorDirection(value: number) {
        this.sd = value;
    }

    public getLinkType(): TopologyLinkTypeEnum {
        return this.t;
    }

    public setLinkType(value: TopologyLinkTypeEnum) {
        this.t = value;
    }

    public getColor(): string {
        return this.c;
    }

    public getSectorInDevice(): number {
        return this.sid;
    }

    public getAssignedName0(): string {
        return this.an0;
    }

    public getAssignedName1(): string {
        return this.an1;
    }

    public getNode0IPAddress(): string {
        return this.ip0;
    }

    public getNode1IPAddress(): string {
        return this.ip1;
    }

    public getSectorPolarity(): SectorPolarityEnum {
        return this.sp ?? SectorPolarityEnum.UNSPECIFIED;
    }
}
